import * as Highcharts from 'highcharts';
import {ready} from './js/util';
import shared from './js/shared';
import cb from './js/cb';
import eo from './js/eo';
import home from './js/home';
import manga from './js/manga';
import nw from './js/nw';
import runs from './js/runs';
import books from './js/books';
import alcohol from './js/alcohol';
import transactions from './js/transactions';
import invoice from './js/invoice';
import mc from './js/mc';
import stardew from './js/stardew';
import svFarmer from './js/sv-farmer';
import svFarmMap from './js/stardew-map';
import trakt from './js/trakt';
import lastfm from './js/lastfm';  // Phase 142
import oura from './js/oura';    // Phase 161
import goals from './js/goals';  // Phase 163
import mood from './js/mood';    // Phase 173
import nudges from './js/nudges';  // Phase 171
import budgets from './js/budgets';  // Phase 192
import tracking from './js/tracking';  // Phase 196
import correlations from './js/correlations';  // Phase 164
import ask from './js/ask';                    // Phase 167
import toolboxBenchmarks from './js/toolbox-benchmarks';
import toolboxCompare from './js/toolbox-compare';
import toolboxRoute from './js/toolbox-route';
import sidebar from './js/sidebar';  // Phase 182
import './js/pwa';

//css
import '@melloware/coloris/dist/coloris.css';  // Phase 178: coloris picker CSS BEFORE style.scss so tokens can override coloris chrome
// Phase 183 (D-05/D-06): Inter Variable + JetBrains Mono Variable — wght axis only (NOT bare import; bare doubles file size)
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/jetbrains-mono/wght.css';
import './css/bootstrap.scss';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import 'datatables.net-responsive-bs5/css/responsive.bootstrap5.min.css';
import 'flatpickr/dist/flatpickr.min.css'
import 'choices.js/public/assets/styles/choices.css';
import 'awesome-notifications/dist/style.css';
import '@yaireo/tagify/dist/tagify.css';
import './css/style.scss';
import './css/mc.scss';
import './css/eo/paperdoll.scss';

// LOAD-BEARING: useUTC:false makes all chart axes/tooltips + standalone Highcharts.dateFormat()
// calls (crosshair-sync.ts, emptyAwareTooltip.ts) render in the host tz (America/Toronto). Dropping
// it is a SILENT no-op regression (host tz IS Toronto). The `as any` is required because HC12 removed
// `useUTC` from the TimeOptions type (D-08) — it is NOT optional; do not remove it.
Highcharts.setOptions({
    time: { useUTC: false } as any,   // LOAD-BEARING — do not remove or move
    lang: { locale: 'en-CA' },
    // D-01/D-02/D-03: single global breakpoint; restricted keys only (chart.height, legend,
    // axis labels). MUST NOT include tooltip/series/chart.type/plotOptions — those trigger
    // propsRequireUpdateSeries and risk wiping emptyAwareTooltip/crosshair-sync pointer patches
    // (CHRT-03 guardrail, PITFALLS §5).
    responsive: {
        rules: [{
            condition: { maxWidth: 540 },
            chartOptions: {
                chart: { height: 220 },
                legend: { enabled: false },
                xAxis: {
                    labels: {
                        rotation: -45,
                        step: 2,
                        style: { fontSize: '10px' },
                    },
                },
                yAxis: {
                    labels: {
                        style: { fontSize: '10px' },
                    },
                },
                // MUST NOT include: tooltip, series, chart.type, plotOptions (CHRT-03 guardrail)
            },
        }],
    },
    // D-06: global touch tooltip — at TOP LEVEL of setOptions, NOT inside responsive.rules.chartOptions
    // (per RESEARCH Pitfall 4 / D-06). followTouchMove:false stops chart from eating vertical scroll
    // (SC#4); stickOnContact:true gives tap-to-reveal-and-stick (CHRT-02).
    tooltip: {
        followTouchMove: false,
        stickOnContact: true,
    },
} as any);

ready(() => {
    shared.init();
    cb.init();
    eo.init();
    home.init();
    manga.init();
    nw.init();
    runs.init();
    books.init();
    alcohol.init();
    transactions.init();
    invoice.init();
    mc.init();
    stardew.init();
    svFarmer.init();
    svFarmMap.init();
    trakt.init();
    lastfm.init();  // Phase 142
    oura.init();   // Phase 161
    goals.init();  // Phase 163
    mood.init();   // Phase 173
    nudges.init();  // Phase 171
    budgets.init();  // Phase 192
    tracking.init();  // Phase 196
    correlations.init();  // Phase 164
    ask.init();           // Phase 167
    toolboxBenchmarks.init();
    toolboxCompare.init();
    toolboxRoute.init();
    sidebar.init();  // Phase 182
});

// Phase 124 — iOS body-scroll-lock for offcanvas sidebar.
// Bootstrap's overflow:hidden on <body> is insufficient on iOS Safari (PITFALLS §14).
// position:fixed + top:-scrollY is the canonical fix. Event listeners are module-scope
// (like the sprite-404 listener below) so they are active before first user interaction.
let _scrollYBeforeLock = 0;

document.addEventListener('show.bs.offcanvas', (e: Event) => {
    const offcanvas = e.target as HTMLElement;
    if (offcanvas.id !== 'sidebar-offcanvas') return;  // guard — only sidebar, not future offcanvas elements
    _scrollYBeforeLock = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${_scrollYBeforeLock}px`;
    document.body.style.width = '100%';
});

document.addEventListener('hide.bs.offcanvas', (e: Event) => {
    const offcanvas = e.target as HTMLElement;
    if (offcanvas.id !== 'sidebar-offcanvas') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, _scrollYBeforeLock);
});

// Phase 107 Plan 05 — global capture-phase image-404 beacon (D-05b, D-05c).
// Resource-load errors do NOT bubble (RESEARCH Pitfall 2) — capture phase is REQUIRED.
// Module-scope Set<string> dedup prevents beacon hammering on bulk failures (Pitfall 4).
const sprite404Seen = new Set<string>();
const CDN_HOST = 'stats.tor1.cdn.digitaloceanspaces.com';

window.addEventListener('error', (ev: Event) => {
    const t = ev.target as HTMLImageElement | null;
    if (!t || !(t instanceof HTMLImageElement)) return;
    const src = t.currentSrc || t.src;
    if (!src || !src.includes(CDN_HOST) || src.includes('/_missing.png')) return;
    if (sprite404Seen.has(src)) return;
    sprite404Seen.add(src);
    const type = t.dataset.spriteType || 'unknown';
    const key = t.dataset.spriteKey || src.split('/').pop() || src;
    fetch('/api/sprite-404', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, key, url: src }),
        keepalive: true,
    }).catch(() => { /* swallow — beacon is fire-and-forget */ });
}, true);

// Phase 126 — SW registration + update-toast (D-06, D-09).
// Gate: only register on the prod origin — skip localhost/ddev to avoid dev cache footgun.
// On controllerchange (skipWaiting+clientsClaim activates new SW immediately), show a
// one-tap "New version available — Reload" toast.

// In-memory reload guard (WR-02): resets to false on every page load, UNLIKE the old
// sessionStorage flag which persisted for the whole session and silently swallowed the
// NEXT deploy's controllerchange. Module scope so the listener and showUpdateToast() share it.
let swReloading = false;

if ('serviceWorker' in navigator) {
    const isProd = window.location.hostname === 'stats.ksat.ca';

    if (isProd) {
        // WR-01: is this page ALREADY controlled by a SW at load? clientsClaim() fires
        // controllerchange on the FIRST install too (no prior controller) — that is the
        // initial claim, NOT a new version, so it must not toast.
        const hadController = !!navigator.serviceWorker.controller;

        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js', { scope: '/' })
                .catch(() => { /* swallow — SW registration failure must never break the app */ });
        });

        // controllerchange fires when a new SW takes control (after skipWaiting+clientsClaim).
        // Pitfall 4: listener attached synchronously inside the gate so it is present before
        // any update-cycle controllerchange fires.
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (swReloading) return;     // re-entrancy guard within this page lifetime (WR-02)
            if (!hadController) return;  // first-install claim, not an update (WR-01)
            showUpdateToast();
        });
    }
}

function showUpdateToast(): void {
    // Reuse the Phase-125 Bootstrap alert + btn-close idiom (D-07, PATTERNS §Bootstrap Alert).
    // Dynamically created — toast fires post-load only, no pre-declared Latte element needed.
    const banner = document.createElement('div');
    banner.className = 'alert alert-warning py-2 px-3 mt-2 small position-fixed bottom-0 end-0 m-3';
    banner.style.zIndex = '9999';
    banner.setAttribute('role', 'alert');
    banner.innerHTML =
        '<span>New version available</span>' +
        '<button type="button" class="btn btn-sm btn-primary ms-2" id="sw-reload-btn">Reload</button>' +
        '<button type="button" class="btn-close btn-close-sm ms-2 float-end" id="sw-dismiss-btn" aria-label="Dismiss"></button>';
    document.body.appendChild(banner);

    document.getElementById('sw-reload-btn')?.addEventListener('click', () => {
        // In-memory reload guard (WR-02): set before reload so a re-entrant controllerchange in
        // this same page lifetime is ignored; resets to false on the next page load so future
        // deploys still toast.
        swReloading = true;
        window.location.reload();
    });

    document.getElementById('sw-dismiss-btn')?.addEventListener('click', () => {
        // One-time dismiss — no localStorage (toast reappears on next deploy signal).
        banner.remove();
    });
}
